// src/pages/documents/probatedocs.service.ts

import api from '../../services/api';
import { PdfFieldMapping } from '../../types';

export interface ExtractFieldsResponse {
  message: string;
  fields: PdfFieldMapping[];
}

export interface UpdateFieldMappingPayload {
  relatedEntity: string | null;
  databaseFieldName: string | null;
}

export const probateDocsService = {
  /**
   * Extracts all form fields from a PDF document and saves them to the database.
   * @param probateDocId - The UID of the probate document
   * @returns Promise with extracted fields
   */
  extractFields: async (probateDocId: string): Promise<ExtractFieldsResponse> => {
    const response = await api.post(`/probatedocs/${probateDocId}/extract-fields`);
    return response.data;
  },

  /**
   * Retrieves all field mappings for a specific probate document.
   * @param probateDocId - The UID of the probate document
   * @returns Promise with field mappings array
   */
  getFieldMappings: async (probateDocId: string): Promise<PdfFieldMapping[]> => {
    const response = await api.get(`/probatedocs/${probateDocId}/field-mappings`);
    return response.data;
  },

  /**
   * Updates the mapping for a specific field.
   * @param probateDocId - The UID of the probate document
   * @param fieldId - The UID of the field to update
   * @param mapping - The mapping data to update
   * @returns Promise with updated field mapping
   */
  updateFieldMapping: async (
    probateDocId: string,
    fieldId: string,
    mapping: UpdateFieldMappingPayload
  ): Promise<PdfFieldMapping> => {
    const response = await api.put(
      `/probatedocs/${probateDocId}/field-mappings/${fieldId}`,
      mapping
    );
    return response.data;
  },

  /**
   * Downloads the annotated PDF with field markers.
   * @param probateDocId - The UID of the probate document
   * @returns Promise that triggers file download
   */
  downloadAnnotatedPdf: async (probateDocId: string): Promise<void> => {
    const response = await api.get(`/probatedocs/${probateDocId}/download/annotated`, {
      responseType: 'blob',
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from headers or set a default
    const disposition = response.headers['content-disposition'];
    let filename = `annotated_${probateDocId}.pdf`;
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameMatch = disposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Gets the URL for the original PDF document for viewing in PDF.js
   * @param probateDocId - The UID of the probate document
   * @returns The URL string for the PDF
   */
  getPdfUrl: (probateDocId: string): string => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000/api';
    return `${baseUrl}/probatedocs/${probateDocId}/download/original`;
  },
};

export default probateDocsService;